// tslint:disable-next-line:no-var-requires
import { warn } from '@readcloud/common';
import { tryCatch } from 'ramda';
import rangy from 'rangy';
import 'rangy/lib/rangy-serializer';
import {
  fromEvent,
  merge as mergeObservables,
  Observable,
  Subject,
} from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  merge,
  scan,
  throttleTime,
} from 'rxjs/operators';

export const serializeRange = (
  range: RangyRange,
  omitHash: boolean,
  root: HTMLElement
): string => (rangy as any).serializeRange(range, omitHash, root);

export const deserializeRange = (
  serializedRange: string,
  root: HTMLElement
): RangyRange | undefined =>
  (rangy as any).deserializeRange(serializedRange, root);

export const canDeserializeRange = (
  serializedRange: string,
  root: HTMLElement
): boolean => (rangy as any).canDeserializeRange(serializedRange, root);

export const nativeRangeToSelection = (range: Range, window?: Window) => {
  const selection = rangy.getSelection(window);
  selection.removeAllRanges();
  selection.addRange(range);
  return selection;
};

export const getSelectionForWindow = (window: Window) =>
  rangy.getSelection(window);

/* Ensure range is within the container, and can be serialised/deserialised */
export const sanitiseRange = (
  rangyRange: RangyRange,
  container: HTMLElement
) => {
  const wholeElementRange = rangy.createRange();
  wholeElementRange.selectNodeContents(container);
  const intersection = tryCatch(
    () => wholeElementRange.intersection(rangyRange),
    () => undefined
  )();
  const serializedRange = intersection
    ? serializeRange(intersection, true, container)
    : null;
  // tslint:disable:no-debugger
  if (!serializedRange || !canDeserializeRange(serializedRange, container)) {
    return null;
  }
  const refriedRange = deserializeRange(serializedRange, container);
  if (!refriedRange) {
    return null;
  }
  return {
    serializedRange,
    rangyRange: refriedRange,
  };
};

/**
 * Returns an Observable stream of text selections in the current document.
 *
 * New values are emitted when the user finishes making a selection
 * (represented by a `DOMRange`) or clears a selection (represented by `null`).
 *
 * A value will be emitted with the selected range at the time of subscription
 * on the next tick.
 *
 * @return Observable<DOMRange|null>
 */
export const getSelectionStream = (
  element = document.body,
  window?: Window
) => {
  // Get a stream of selection changes that occur whilst the user is not
  // making a selection with the mouse.
  const pointerdown$ = fromEvent(element, 'pointerdown');
  const pointerup$ = fromEvent(element, 'pointerup');

  const selectionchange$ = fromEvent(
    element.ownerDocument || document,
    'selectionchange'
  ); // Selection change event must be on document

  let isMouseDown = false;
  const selectionTriggerEvents = mergeObservables(
    pointerdown$,
    pointerup$,
    selectionchange$.pipe(throttleTime(100))
  );

  return selectionTriggerEvents.pipe(
    merge(clearSelection$),
    map((event: Event) => {
      if (event) {
        if (event.type === 'pointerup') {
          isMouseDown = false;
        }
        if (event.type === 'pointerdown') {
          isMouseDown = true;
        }
      }

      let selection;
      try {
        selection = rangy.getSelection(window);
      } catch (e) {
        warn('Could not get selection', e);
      }

      const rangyRange =
        event && selection && selection.rangeCount > 0
          ? selection.getRangeAt(0)
          : null;
      const nativeRange = (rangyRange && rangyRange.nativeRange) || null;
      const selectionFinished = !isMouseDown;
      const startsWithinElement = nativeRange
        ? element.contains(nativeRange.startContainer)
        : false;
      return {
        containedWithinElement: startsWithinElement,
        ...(nativeRange &&
        rangyRange &&
        selectionFinished &&
        startsWithinElement
          ? (() => {
              const wholeElementRange = rangy.createRange();
              wholeElementRange.selectNodeContents(element);
              const intersection = wholeElementRange.intersection(rangyRange);
              const serializedRange = intersection
                ? serializeRange(intersection, true, element)
                : null;
              const refriedRange =
                serializedRange && canDeserializeRange(serializedRange, element)
                  ? deserializeRange(serializedRange, element)
                  : null;
              return {
                serializedRange,
                rangyRange: refriedRange,
                nativeRange: refriedRange ? refriedRange.nativeRange : null,
              };
            })()
          : {}),
        event,
        rangySelection: selection,
        selectionFinished,
      };
    }),
    debounceTime(50),
    scan(
      (acc: any, next) => {
        acc.prev = acc.current;
        acc.current = next;
        return acc;
      },
      {
        current: null,
        prev: null,
      }
    ),
    filter(({ current, prev }) => {
      return (
        current &&
        prev &&
        (current.nativeRange !== prev.nativeRange ||
          current.selectionFinished !== prev.selectionFinished)
      );
    }),
    map(({ current, prev }) => {
      return current;
    })
  ) as Observable<{
    nativeRange?: Range;
    selectionFinished: boolean;
    rangyRange?: RangyRange;
    rangySelection?: RangySelection;
    serializedRange?: string;
    event: Event;
    containedWithinElement?: boolean;
  }>;
};

/** Returns the selected `DOMRange` in `document`. */
export function selectedRange(doc = document) {
  const selection = doc.getSelection();
  if (
    !selection ||
    !selection.rangeCount ||
    selection.getRangeAt(0).collapsed
  ) {
    return null;
  } else {
    return selection.getRangeAt(0);
  }
}

export const clearSelection$ = new Subject<any>();
clearSelection$.subscribe();

export const clearSelection = (win: Window = window) => {
  const currentSelection = win.getSelection && win.getSelection();
  if (currentSelection) {
    currentSelection.removeAllRanges();
  } else if ((win.document as any).selection) {
    (win.document as any).selection.empty();
  }
  clearSelection$.next(null);
};

export function clearGlobalSelection() {
  if (window.getSelection) {
    const currentSelection = window.getSelection();
    if (!currentSelection || currentSelection.empty) {
      // Chrome
      if (currentSelection) currentSelection.empty();
    } else if (currentSelection.removeAllRanges) {
      // Firefox
      currentSelection.removeAllRanges();
    }
  } else if ((document as any).selection) {
    // IE?
    (document as any).selection.empty();
  }
}

/* export function selectText(node: Node) {
  if ((document.body as any).createTextRange) {
    const range = (document.body as any).createTextRange();
    range.moveToElementText(node);
    range.select();
  } else if (window.getSelection) {
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(node);
    selection.removeAllRanges();
    selection.addRange(range);
  } else {
    warn("Could not select text in node: Unsupported browser.");
  }
} */

/* export const selectRange = (range: Range) => {
  if ((document.body as any).createTextRange) {
    (range as any).select();
  } else if (window.getSelection) {
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  } else {
    warn("Could not select text in node: Unsupported browser.");
  }
};
 */
